import { useEffect, useRef, useState } from 'react'

import { useInfiniteLandingExpertsCarouselInView } from '../hooks/useInfiniteLandingExpertsCarouselInView'
import { getWrappedIndex } from '../utils/getWrappedIndex'

const CIRCLE_ASSISTANT_IMAGE = '/images/infinite/circle/circle_assistant.png'
const MUTUAL_FUND_EXPERT_IMAGE = '/images/infinite/circle/mutual_fund_expert.png'
const CIRCLE_MANAGER_IMAGE = '/images/infinite/circle/circle_manager.png'
const INSURANCE_EXPERT_IMAGE = '/images/infinite/circle/insurance_expert.png'
const INCOME_TAX_EXPERT_IMAGE = '/images/infinite/circle/income_tax_expert.png'
const LIABILITIES_EXPERT_IMAGE = '/images/infinite/circle/liabilities_expert.png'

const CAROUSEL_TRANSITION_MS = 2500
const TESTIMONIAL_STEP_DURATION_MS = 3400
const TESTIMONIAL_TEXT_PHASE_DURATION_MS = CAROUSEL_TRANSITION_MS / 2

export const INFINITE_LANDING_EXPERTS: {
  name: string
  type: 'icon' | 'infi'
  image: string
  showComingSoon?: boolean
}[] = [
    { name: 'Circle Manager', type: 'infi', image: CIRCLE_MANAGER_IMAGE },
    { name: 'Mutual Fund Expert', type: 'icon', image: MUTUAL_FUND_EXPERT_IMAGE },
    { name: 'Circle Assistant', type: 'icon', image: CIRCLE_ASSISTANT_IMAGE },
    { name: 'Insurance Expert', type: 'infi', image: INSURANCE_EXPERT_IMAGE },
    { name: 'Income Tax Expert', type: 'infi', image: INCOME_TAX_EXPERT_IMAGE },
    { name: 'Liabilities Expert', type: 'infi', image: LIABILITIES_EXPERT_IMAGE }
  ]

type InfiniteLandingExpertsOrbitCarouselProps = {
  carouselTrackClassName?: string
  withInitialStepDelay?: boolean
}

export function InfiniteLandingExpertsOrbitCarousel({
  carouselTrackClassName = 'relative mt-9 h-[150px] overflow-hidden',
  withInitialStepDelay = false
}: InfiniteLandingExpertsOrbitCarouselProps) {
  const { carouselRef, hasEnteredViewport } = useInfiniteLandingExpertsCarouselInView()
  const [activeIndex, setActiveIndex] = useState(0)
  const [renderedLabel, setRenderedLabel] = useState('')
  const [isLabelVisible, setIsLabelVisible] = useState(false)
  const syncedActiveIndexRef = useRef<number | null>(null)
  const labelSwapTimeoutRef = useRef<number | null>(null)
  const labelFadeInFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (!hasEnteredViewport) {
      syncedActiveIndexRef.current = null
      setRenderedLabel('')
      setIsLabelVisible(false)
      return
    }

    const nextLabel = INFINITE_LANDING_EXPERTS[activeIndex]?.name ?? ''

    if (syncedActiveIndexRef.current === null) {
      syncedActiveIndexRef.current = activeIndex
      setRenderedLabel(nextLabel)
      setIsLabelVisible(true)
      return
    }

    if (syncedActiveIndexRef.current === activeIndex) {
      return
    }

    if (labelSwapTimeoutRef.current) {
      window.clearTimeout(labelSwapTimeoutRef.current)
      labelSwapTimeoutRef.current = null
    }
    if (labelFadeInFrameRef.current) {
      window.cancelAnimationFrame(labelFadeInFrameRef.current)
      labelFadeInFrameRef.current = null
    }

    setIsLabelVisible(false)
    labelSwapTimeoutRef.current = window.setTimeout(() => {
      setRenderedLabel(nextLabel)
      syncedActiveIndexRef.current = activeIndex
      labelFadeInFrameRef.current = window.requestAnimationFrame(() => {
        setIsLabelVisible(true)
      })
    }, TESTIMONIAL_TEXT_PHASE_DURATION_MS)

    return () => {
      if (labelSwapTimeoutRef.current) {
        window.clearTimeout(labelSwapTimeoutRef.current)
        labelSwapTimeoutRef.current = null
      }
      if (labelFadeInFrameRef.current) {
        window.cancelAnimationFrame(labelFadeInFrameRef.current)
        labelFadeInFrameRef.current = null
      }
    }
  }, [activeIndex, hasEnteredViewport])

  useEffect(() => {
    if (!hasEnteredViewport) {
      return
    }

    if (withInitialStepDelay) {
      const initialMoveTimeout = window.setTimeout(() => {
        setActiveIndex((previousIndex) => getWrappedIndex(previousIndex + 1, INFINITE_LANDING_EXPERTS.length))
      }, 250)

      const interval = window.setInterval(() => {
        setActiveIndex((previousIndex) => getWrappedIndex(previousIndex + 1, INFINITE_LANDING_EXPERTS.length))
      }, TESTIMONIAL_STEP_DURATION_MS)

      return () => {
        window.clearTimeout(initialMoveTimeout)
        window.clearInterval(interval)
      }
    }

    const interval = window.setInterval(() => {
      setActiveIndex((previousIndex) => getWrappedIndex(previousIndex + 1, INFINITE_LANDING_EXPERTS.length))
    }, TESTIMONIAL_STEP_DURATION_MS)

    return () => window.clearInterval(interval)
  }, [hasEnteredViewport, withInitialStepDelay])

  return (
    <div ref={carouselRef} className='pointer-events-none'>
      <div className={carouselTrackClassName}>
        {hasEnteredViewport
          ? INFINITE_LANDING_EXPERTS.map((item, index) => {
            const relativeOffset = getRelativeOffset(index, activeIndex, INFINITE_LANDING_EXPERTS.length)
            const coverflowStyle = getCoverflowStyle(relativeOffset)

            return (
              <div
                key={item.name}
                className='absolute left-[calc(50%-75px)] top-1/2 transition-[transform,opacity,filter] duration-[2500ms] ease-[cubic-bezier(0.38,0.15,0.01,1)]'
                style={coverflowStyle}
              >
                <InfiniteLandingExpertOrbitItem item={item} isCenter={relativeOffset === 0} />
              </div>
            )
          })
          : null}
      </div>
      <p
        className={`mt-5 min-h-[18px] text-center font-libre-franklin text-[15px] font-medium text-white transition-opacity duration-[2500ms] ease-[cubic-bezier(0.38,0.15,0.01,1)] [will-change:opacity] ${hasEnteredViewport && isLabelVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        {renderedLabel}
      </p>
    </div>
  )
}

export function InfiniteLandingExpertOrbitItem({
  item,
  isCenter
}: {
  item: { name: string; type: 'icon' | 'infi'; image: string; showComingSoon?: boolean }
  isCenter: boolean
}) {
  return (
    <div
      className={`relative size-[150px] transition-[transform,opacity,filter] duration-[2500ms] ease-[cubic-bezier(0.38,0.15,0.01,1)] ${isCenter ? 'scale-100 blur-0 opacity-100' : 'scale-[0.5] blur-[3px] opacity-60'}`}
    >
      {item.type === 'icon' ? (
        <div className='size-full rounded-full border border-white'>
          <img src={item.image} alt={item.name} className='size-full rounded-full object-cover' />
        </div>
      ) : (
        <>
          <img src={item.image} alt={item.name} className='absolute inset-0 size-full object-contain' />
          {item.showComingSoon ? (
            <p className='absolute left-1/2 top-[43.51px] w-[65px] -translate-x-1/2 text-center font-libre-franklin text-[10px] font-bold leading-[1.3] tracking-[0.1px] text-white [text-shadow:5px_2px_7.6px_black]'>
              Coming Soon
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

export function getCoverflowStyle(
  relativeOffset: number
): { zIndex: number; transform: string; opacity: number; visibility: 'visible' | 'hidden' } {
  if (relativeOffset === 0) {
    return {
      zIndex: 30,
      transform: 'translateY(-50%) translateX(0px) scale(1)',
      opacity: 1,
      visibility: 'visible'
    }
  }

  if (relativeOffset === -1) {
    return {
      zIndex: 20,
      transform: 'translateY(-50%) translateX(-138px) scale(0.68)',
      opacity: 0.5,
      visibility: 'visible'
    }
  }

  if (relativeOffset === 1) {
    return {
      zIndex: 20,
      transform: 'translateY(-50%) translateX(138px) scale(0.68)',
      opacity: 0.5,
      visibility: 'visible'
    }
  }

  return {
    zIndex: 0,
    transform: `translateY(-50%) translateX(${relativeOffset < 0 ? -260 : 260}px) scale(0.48)`,
    opacity: 0,
    visibility: 'hidden'
  }
}

export function getRelativeOffset(index: number, activeIndex: number, total: number) {
  const forward = getWrappedIndex(index - activeIndex, total)
  const backward = forward - total
  if (Math.abs(forward) <= Math.abs(backward)) {
    return forward
  }

  return backward
}
